/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: input.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Input component for form fields with consistent styling,
    validation states, and accessibility features.

 🔧 Notes:
    - Implements forwardRef for proper ref handling
    - Supports different input types and validation states
    - Integrates with Tailwind CSS for consistent styling
    - Provides accessibility features and keyboard navigation
    - Supports custom className and all HTML input attributes

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { InputHTMLAttributes, forwardRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className = '', error, ...props }, ref) => {
    return (
      <div className="w-full">
        <input
          ref={ref}
          className={`flex h-10 w-full rounded-md border ${
            error
              ? 'border-destructive text-foreground focus-visible:border-destructive focus-visible:ring-destructive'
              : 'border-input text-foreground focus-visible:border-input focus-visible:ring-ring'
          } bg-background px-3 py-2 text-sm placeholder:text-muted-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
          {...props}
        />
        {error && (
          <p className="mt-1.5 text-xs font-medium text-red-500 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
